import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import {
  FileUpload,
  Loader,
  SdirCheckbox,
  SdirModal,
  SdirRadioButton,
  SdiTrash,
  Textarea,
  TextLink,
} from '@sdir/sds';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { InputError } from '@sdir/blueprint.aps/lib/components';
// eslint-disable-next-line max-len
import { EndorsementServiceFeaturesGetApplicationResponse } from '@sdir/httpclient/lib/clients/aps/endorsement';
import FileName from '../Atoms/FileName';
import { RequirementType } from '../../constants/enums';

export type ClarifyInput = {
  uid: string;
  nisCourse?: File[];
  nationalCertificate?: File[];
  vesselGrossTonnageLessThan250?: boolean;
  dispensation?: boolean;
  declarationOfCompetence?: boolean;
  comment?: string;
};

interface ClarifyModalProps {
  application?: EndorsementServiceFeaturesGetApplicationResponse;
  error: any;
  loading: boolean;
  show: boolean;
  onClose: () => void;
  onSubmit: (input: ClarifyInput) => void;
}

const ClarifyModal: React.FC<ClarifyModalProps> = ({
  application,
  error,
  loading,
  show,
  onClose,
  onSubmit,
}) => {
  const intl = useIntl();
  const [comment, setComment] = useState<string>();
  const [grossTonnage, setGrossTonnage] = useState<string>('');
  const [nisCourseAttachments, setNisCourseAttachments] = useState<File[]>([]);
  const [validationErrors, setValidationErrors] = useState<any>();
  const [nationalCertificateAttachments, setNationalCertificateAttachments] = useState<File[]>([]);
  const [checkedDispensation, setCheckedDispensation] = useState<boolean>(false);
  const [confirmDeclaration, setConfirmDeclaration] = useState<boolean>(false);

  const message = useMemo(() => {
    if (!application?.messages || application.messages.length === 0) return '';
    const messageItem = application?.messages?.[application.messages.length - 1];
    return messageItem.content || '';
  }, [application?.messages]);

  const { showNisCourse, showNationalCertificate, showGrossTonnage, showDeclaration } =
    useMemo(() => {
      const nisRequirement = application?.requirements?.find(
        (r) => r.requirementName === RequirementType.NisCourse
      );
      const gtRequirement = application?.requirements?.find(
        (r) => r.requirementName === RequirementType.GrossTonnageLessThan250
      );
      const dispRequirement = application?.requirements?.find(
        (r) => r.requirementName === RequirementType.Dispensation
      );
      const docRequirement = application?.requirements?.find(
        (r) => r.requirementName === RequirementType.DeclarationOfCompetence
      );
      return {
        showNisCourse: nisRequirement && nisRequirement?.isApproved === false,
        showNationalCertificate: application?.nationalCertificateRequirementApproved === false,
        showGrossTonnage: gtRequirement && dispRequirement && dispRequirement?.isApproved === false,
        showDeclaration: docRequirement && application?.declarationOfCompetence === false,
      };
    }, [application]);

  const certificateName = useMemo(() => {
    return application?.certificate?.nationalCapacityName || '';
  }, [application?.certificate?.nationalCapacityName]);

  const disableSubmit = useMemo(() => {
    if (showGrossTonnage) {
      const prevGrossTonnage =
        application?.vesselGrossTonnageLessThan250 === null
          ? undefined
          : application?.vesselGrossTonnageLessThan250;
      const newGrossTonnage = grossTonnage === '0-250';
      const prevDispensation =
        application?.dispensation === null ? undefined : application?.dispensation;
      if (prevGrossTonnage !== newGrossTonnage) return false;
      if (prevDispensation !== checkedDispensation) return false;
    }

    const allFiles = (nisCourseAttachments || []).concat(nationalCertificateAttachments || []);
    if (!comment && allFiles.length === 0) return true;
    return false;
  }, [
    application,
    checkedDispensation,
    comment,
    grossTonnage,
    nationalCertificateAttachments,
    nisCourseAttachments,
    showGrossTonnage,
  ]);

  const onChangeNisCourse = (files: File[]) => {
    const total = nisCourseAttachments ? [...nisCourseAttachments] : [];
    total.push(files?.[0]);
    setNisCourseAttachments(total);
  };

  const onRemoveNisCourse = (file: File) => {
    let total = nisCourseAttachments ? [...nisCourseAttachments] : [];
    total = total.filter((a) => file.name !== a.name);
    setNisCourseAttachments(total);
    setValidationErrors(undefined);
  };

  const onChangeNationalCertificate = (files: File[]) => {
    const total = nationalCertificateAttachments ? [...nationalCertificateAttachments] : [];
    total.push(files?.[0]);
    setNationalCertificateAttachments(total);
  };

  const onRemoveNationalCertificate = (file: File) => {
    let total = nationalCertificateAttachments ? [...nationalCertificateAttachments] : [];
    total = total.filter((a) => file.name !== a.name);
    setNationalCertificateAttachments(total);
    setValidationErrors(undefined);
  };

  const onChangeComment = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value || '';
    setComment(value);
  };

  const onChangeGrossTonnage = (event: any) => {
    const value = event?.target?.value;
    setGrossTonnage(value);
  };

  const onCloseModal = () => {
    setComment('');
    setNisCourseAttachments([]);
    setNationalCertificateAttachments([]);
    onClose();
  };

  const validate = () => {
    const errors: any = {};
    (nationalCertificateAttachments || []).forEach((a) => {
      if (a?.size === 0) {
        errors.nationalCertificate = intl.formatMessage({ id: 'general.form.error.emptyfile' });
      }
    });
    (nisCourseAttachments || []).forEach((a) => {
      if (a?.size === 0) {
        errors.nisCourse = intl.formatMessage({ id: 'general.form.error.emptyfile' });
      }
    });
    return errors;
  };

  const onSubmitClarification = () => {
    const prevGrossTonnage =
      application?.vesselGrossTonnageLessThan250 === null
        ? undefined
        : application?.vesselGrossTonnageLessThan250;
    const newGrossTonnage = grossTonnage === '0-250';
    const prevDispensation =
      application?.dispensation === null ? undefined : application?.dispensation;
    const prevDeclaration =
      application?.declarationOfCompetence === null
        ? undefined
        : application?.declarationOfCompetence;
    const input: ClarifyInput = {
      uid: application?.id || '',
      nisCourse: nisCourseAttachments || [],
      nationalCertificate: nationalCertificateAttachments || [],
      vesselGrossTonnageLessThan250: showGrossTonnage ? newGrossTonnage : prevGrossTonnage,
      dispensation: showGrossTonnage ? checkedDispensation : prevDispensation,
      declarationOfCompetence: showDeclaration ? confirmDeclaration : prevDeclaration,
      comment,
    };
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors(undefined);
    onSubmit(input);
  };

  useEffect(() => {
    if (application?.vesselGrossTonnageLessThan250) {
      setGrossTonnage('0-250');
    } else {
      setGrossTonnage('250+');
    }
    if (application?.dispensation) {
      setCheckedDispensation(true);
    }
  }, [application?.dispensation, application?.vesselGrossTonnageLessThan250]);

  return (
    <StyledModal
      visible={show}
      width="500px"
      onCancel={onCloseModal}
      onOK={onSubmitClarification}
      okText={intl.formatMessage({ id: 'general.submit' })}
      cancelText={intl.formatMessage({ id: 'general.cancel' })}
      disableOK={disableSubmit}
    >
      <Content>
        <h2>{intl.formatMessage({ id: 'application.clarify.title' })}</h2>
        <Message>{message}</Message>
        {showNisCourse && (
          <Requirement>
            <h3>{intl.formatMessage({ id: 'application.clarify.niscourse' })}</h3>
            <span>{intl.formatMessage({ id: 'application.clarify.upload.niscourse.desc' })}</span>
            <FileUpload
              allowedTypes=".png,.jpg,.jpeg,.pdf"
              buttonLabel={intl.formatMessage({ id: 'application.clarify.upload.niscourse' })}
              onFilesSelected={onChangeNisCourse}
            />
            {nisCourseAttachments?.map((f) => (
              <FileItem key={`file-${f.name}`}>
                <FileName fileName={f.name || ''} />
                <TextLink
                  icon={<SdiTrash size="s" />}
                  text=""
                  onClick={() => onRemoveNisCourse(f)}
                />
              </FileItem>
            ))}
            {validationErrors?.nisCourse && <InputError>{validationErrors?.nisCourse}</InputError>}
          </Requirement>
        )}
        {showNationalCertificate && (
          <Requirement>
            <h3>{intl.formatMessage({ id: 'application.clarify.nationalcertificate' })}</h3>
            <span>
              {intl.formatMessage({ id: 'application.clarify.upload.nationalcertificate.desc' })}
              {certificateName}
            </span>
            <FileUpload
              allowedTypes=".png,.jpg,.jpeg,.pdf"
              buttonLabel={intl.formatMessage({
                id: 'application.clarify.upload.nationalcertificate',
              })}
              onFilesSelected={onChangeNationalCertificate}
            />
            {nationalCertificateAttachments?.map((f) => (
              <FileItem key={`file-${f.name}`}>
                <FileName fileName={f.name || ''} />
                <TextLink
                  icon={<SdiTrash size="s" />}
                  text=""
                  onClick={() => onRemoveNationalCertificate(f)}
                />
              </FileItem>
            ))}
            {validationErrors?.nationalCertificate && (
              <InputError>{validationErrors?.nationalCertificate}</InputError>
            )}
          </Requirement>
        )}
        {showGrossTonnage && (
          <GrossTonnageWrapper>
            <RadioContainer as="fieldset" id="radiogroup-grosstonnage">
              <SdirRadioButton
                value="250+"
                checked={grossTonnage === '250+'}
                title={intl.formatMessage({
                  id: 'application.requirements.grossTonnage.option2',
                })}
                onClick={(val) => onChangeGrossTonnage({ target: { value: val } })}
                name="grossTonnage"
              />
              <DispensationWrapper>
                <DispensationDesc>
                  {intl.formatMessage({
                    id: 'application.requirements.dispensation.description',
                  })}
                </DispensationDesc>
                <SdirCheckbox
                  checked={grossTonnage === '0-250' ? false : checkedDispensation}
                  title={intl.formatMessage({
                    id: 'application.requirements.dispensation.checkbox',
                  })}
                  onClick={() => {
                    setCheckedDispensation(!checkedDispensation);
                  }}
                />
              </DispensationWrapper>
              <SdirRadioButton
                value="0-250"
                checked={grossTonnage === '0-250'}
                title={intl.formatMessage({
                  id: 'application.requirements.grossTonnage.option1',
                })}
                onClick={(val) => onChangeGrossTonnage({ target: { value: val } })}
                name="grossTonnage"
              />
            </RadioContainer>
          </GrossTonnageWrapper>
        )}
        {showDeclaration && (
          <DeclarationWrapper>
            <SdirCheckbox
              checked={confirmDeclaration}
              title={intl.formatMessage({
                id: 'application.requirements.declaration.checkbox',
              })}
              onClick={() => setConfirmDeclaration(!confirmDeclaration)}
            />
          </DeclarationWrapper>
        )}
        <Textarea
          placeholder={intl.formatMessage({ id: 'application.clarify.comment' })}
          rows={6}
          value={comment}
          onChange={onChangeComment}
        />
        {loading && (
          <Feedback>
            <Loader />
          </Feedback>
        )}
        {error && (
          <Feedback>
            <ErrorMessage error={error} />
          </Feedback>
        )}
      </Content>
    </StyledModal>
  );
};

export default ClarifyModal;

const StyledModal = styled(SdirModal)`
  max-height: 100vh;
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const Content = styled.div`
  h2,
  h3 {
    margin: 0;
  }
`;

const RadioContainer = styled.fieldset`
  border: none;
`;

const Feedback = styled.div`
  margin: 20px 0;
`;

const GrossTonnageWrapper = styled.div`
  margin-bottom: 40px;
`;

const DeclarationWrapper = styled.div`
  margin-bottom: 40px;
`;

const DispensationWrapper = styled.div`
  padding-left: 55px;
`;

const DispensationDesc = styled.span`
  display: block;
  font-style: italic;
  font-size: 14px;
  line-height: 1.25;
  margin-bottom: 10px;
`;

const Requirement = styled.div`
  margin-bottom: 30px;

  section {
    margin-top: 10px;
  }
`;

const FileItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const Message = styled.span`
  display: block;
  margin: 20px 0 30px 0;
  white-space: pre-wrap;
  font-size: 14px;
  && {
    line-height: 1.25;
  }
`;
